// src/components/Header.js
import React from "react";
import styled from "styled-components";
import { FaRocket } from "react-icons/fa";
// Logo is now served from public directory
import theme from "../styles/theme";
import { withPublicUrl } from "../config";

const HeaderContainer = styled.header`
  width: 100%;
  padding: ${theme.spacing[4]} ${theme.spacing[12]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${theme.gradients.hero};
  position: fixed;
  top: 0;
  z-index: ${theme.zIndex.sticky};
  transition: all ${theme.animation.normal} cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  box-shadow: ${theme.shadows.md};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[4]};
  }
`;

const Logo = styled.img`
  width: 180px;
  height: auto;
  transition: transform ${theme.animation.normal} ease;
  filter: brightness(0) invert(1);
  &:hover {
    transform: scale(1.05);
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: ${theme.spacing[8]};
  align-items: center;

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled.a`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  color: #fff;
  position: relative;
  padding: ${theme.spacing[2]} 0;
  transition: color ${theme.animation.normal} ease;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${theme.gradients.accent};
    transition: width ${theme.animation.normal} cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: ${theme.borderRadius.sm};
  }

  &:hover {
    color: ${theme.primary[100]};
  }

  &:hover::after {
    width: 100%;
  }
`;

const CTAButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: #fff;
  background: ${theme.gradients.accent};
  border: none;
  border-radius: ${theme.borderRadius.full};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.2);
  transition: all ${theme.animation.normal} cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
  }

  &:hover::before {
    left: 100%;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    transition: transform ${theme.animation.normal} ease;
  }

  &:hover svg {
    transform: translateX(2px);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #fff;
  font-size: ${theme.typography.fontSize['2xl']};
  cursor: pointer;
  padding: ${theme.spacing[2]};

  @media (max-width: ${theme.breakpoints.md}) {
    display: block;
  }
`;

const Header = () => {
  // No need for scrolled state since background is always gradient
  return (
    <HeaderContainer>
      <Logo src={withPublicUrl("/logo.png")} alt="ISO Audit Pro - International ISO 17025, ISO 17020 & AISC Accreditation Consulting" />

      <Nav>
        {["Home", "About", "Services", "International", "Contact"].map((item, index) => (
          <NavLink key={index} href={`#${item.toLowerCase()}`}>
            {item}
          </NavLink>
        ))}
        <CTAButton
          onClick={() => {
            window.open("/app", "_blank");
          }}
        >
          Get Started <FaRocket />
        </CTAButton>
      </Nav>

      <MobileMenuButton>☰</MobileMenuButton>
    </HeaderContainer>
  );
};

export default Header;
